/**
 * Service Detail Component
 * - Displays comprehensive service information
 * - Shows service details, image, status, and metadata
 * - Provides edit and delete functionality
 */

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchServiceById, deleteService } from "../../redux/slices/servicesSlice";
import DetailView from "../../components/details/DetailView";
import SectionHeader from "../../components/details/Shared/SectionHeader";
import InfoRow from "../../components/details/Shared/InfoRow";
import ImageModal from "../../components/common/ImageModal";
import { FiPackage, FiFileText, FiImage, FiCheckCircle, FiXCircle, FiCalendar } from "react-icons/fi";
import { toast } from "react-toastify";
import { MdOutlineArrowBackIos } from "react-icons/md";

const ServiceDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { detail: service, detailStatus } = useSelector((state) => state.services);

  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    if (id) {
      dispatch(fetchServiceById(id));
    }
  }, [dispatch, id]);

  if (detailStatus === "loading") return <div className="px-20 pt-10">Loading...</div>;
  if (detailStatus === "failed") return <div className="px-20 pt-10">Failed to load service data.</div>;
  if (!service) return <div className="px-20 pt-10">Service not found.</div>;

  const handleDelete = async () => {
    try {
      await dispatch(deleteService(id)).unwrap();
      toast.success("Service deleted successfully");
      navigate("/swift-app/admin/services");
    } catch (error) {
      toast.error("Failed to delete service");
      console.error("Delete error:", error);
    }
  };

  const handleEdit = () => {
    navigate(`/swift-app/admin/services/${id}/edit`);
  };

  return (
    <div className="relative min-h-screen bg-white font-inter px-6 pt-12 pb-20">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4 md:gap-0 mb-10">
        <div className="flex items-start">
          <button
            onClick={() => navigate(-1)}
            aria-label="Go Back"
            className="mt-1 mr-3 p-2 text-2xl text-gray-800 hover:text-blue-600"
          >
            <MdOutlineArrowBackIos />
          </button>
          <div>
            <div className="text-xl font-bold text-black">
              Service Details: {service.name}
            </div>
            <div className="text-gray-600 text-sm mt-1">
              View detailed service information
            </div>
          </div>
        </div>

        {/* Desktop buttons - hidden on mobile */}
        <div className="hidden md:flex gap-3 md:self-end">
          <button
            onClick={() => navigate(-1)}
            className="border border-gray-300 rounded-lg px-4 py-2 text-base font-medium text-gray-700 bg-white hover:bg-gray-50 transition"
          >
            Back to Services
          </button>
          <button
            onClick={handleEdit}
            className="bg-[#347DFF] text-white rounded-lg px-5 py-2 text-base font-medium hover:bg-blue-600 transition"
          >
            Edit Service
          </button>
        </div>
      </div>

      {/* Service Content */}
      <div className="space-y-8 px-10">
        {/* Service Header with Image and Status */}
        <div className="flex flex-col sm:flex-row gap-6 items-start">
          {/* Service Image */}
          <div className="flex-shrink-0">
            {service.product_image_url ? (
              <img
                src={service.product_image_url}
                alt={service.name}
                className="w-32 h-32 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => setSelectedImage(service.product_image_url)}
              />
            ) : (
              <div className="w-32 h-32 bg-gray-200 rounded-lg flex items-center justify-center">
                <FiImage className="w-12 h-12 text-gray-400" />
              </div>
            )}
          </div>

          {/* Service Info */}
          <div className="flex-1 space-y-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{service.title}</h2>
              <p className="text-lg text-gray-600 mt-1">{service.name}</p>
            </div>

            {/* Status Badge */}
            <div className="flex items-center gap-2">
              {service.active ? (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  <FiCheckCircle className="w-4 h-4 mr-1" />
                  Active
                </span>
              ) : (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                  <FiXCircle className="w-4 h-4 mr-1" />
                  Inactive
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Service Description */}
        <div>
          <SectionHeader>Description</SectionHeader>
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-gray-700 whitespace-pre-wrap">{service.description}</p>
          </div>
        </div>

        {/* Service Metadata */}
        <div>
          <SectionHeader>Service Information</SectionHeader>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoRow icon={FiPackage}>
              Service Name: {service.name}
            </InfoRow>
            <InfoRow icon={FiFileText}>
              Title: {service.title}
            </InfoRow>
            <InfoRow icon={FiCalendar}>
              Created: {new Date(service.created_at).toLocaleDateString()}
            </InfoRow>
            <InfoRow icon={FiCalendar}>
              Updated: {new Date(service.updated_at).toLocaleDateString()}
            </InfoRow>
          </div>
        </div>

        {/* Service ID */}
        <div>
          <SectionHeader>Service ID</SectionHeader>
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
            <div className="text-lg font-semibold text-gray-900">
              UUID: {service.uuid}
            </div>
          </div>
        </div>

        {/* Mobile buttons - shown only on mobile after Service ID */}
        <div className="md:hidden flex flex-col gap-3 pt-6 border-t border-gray-200">
          <button
            onClick={handleEdit}
            className="w-full bg-[#347DFF] text-white rounded-lg px-5 py-3 text-base font-medium hover:bg-blue-600 transition"
          >
            Edit Service
          </button>
          <button
            onClick={() => navigate(-1)}
            className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base font-medium text-gray-700 bg-white hover:bg-gray-50 transition"
          >
            Back to Services
          </button>
        </div>
      </div>

      {/* Image Modal */}
      <ImageModal
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        imageSrc={selectedImage}
        altText={`${service.name} image`}
      />

      {/* Hidden DetailView for consistent delete functionality */}
      <div style={{ display: 'none' }}>
        <DetailView
          title={`Service: ${service.name}`}
          type="service"
          data={service}
          onDelete={handleDelete}
          onEdit={handleEdit}
        />
      </div>
    </div>
  );
};

export default ServiceDetail;
